import {useEffect} from 'react'

import {
  subscribeOnBuildTypeStatus,
  subscribeOnPipelineStatus,
  subscribeOnProjectStatus,
} from '../actions/statuses'
import {usePipelineInternalId} from '../components/pages/PipelinesPages/hooks/pipeline'
import {getBuildType, getProject} from '../selectors'
import type {Branch} from '../services/rest'
import type {
  BuildTypeId,
  BuildTypeInternalId,
  PipelineId,
  ProjectId,
  ProjectInternalId,
} from '../types'

import {useBranch} from './routes'

import {useAppDispatch, useAppSelector} from 'src/hooks/react-redux'

type Props = {
  projectId?: ProjectId | null | undefined
  buildTypeId?: BuildTypeId | null | undefined
  pipelineId?: PipelineId | null | undefined
  projectInternalId?: ProjectInternalId | null | undefined
  buildTypeInternalId?: BuildTypeInternalId | null | undefined
  pipelineInternalId?: ProjectInternalId | null | undefined
  branch?: Branch | null | undefined
  withoutBranch?: boolean
  blockUpdates?: boolean
  timeout?: number | undefined
}

export default (props: Props) => {
  const {projectId, buildTypeId, pipelineId, branch, withoutBranch, blockUpdates, timeout} = props
  const dispatch = useAppDispatch()

  const branchFromQuery = useBranch()
  const internalBranch = branch == null && withoutBranch !== true ? branchFromQuery : branch

  const projectInternalId = useAppSelector(
    state => props.projectInternalId ?? getProject(state, projectId)?.internalId,
  )
  const buildTypeInternalId = useAppSelector(
    state => props.buildTypeInternalId ?? getBuildType(state, buildTypeId)?.internalId,
  )

  useEffect(() => {
    if (blockUpdates !== true && projectId && projectInternalId) {
      return dispatch(
        subscribeOnProjectStatus(projectId, projectInternalId, internalBranch, timeout),
      )
    }
    return undefined
  }, [projectId, projectInternalId, internalBranch, blockUpdates, timeout, dispatch])

  useEffect(() => {
    if (blockUpdates !== true && buildTypeId && buildTypeInternalId) {
      return dispatch(
        subscribeOnBuildTypeStatus(buildTypeId, buildTypeInternalId, internalBranch, timeout),
      )
    }
    return undefined
  }, [buildTypeId, buildTypeInternalId, internalBranch, blockUpdates, timeout, dispatch])

  const pipelineInternalId = usePipelineInternalId(pipelineId, props.pipelineInternalId)
  useEffect(() => {
    if (blockUpdates !== true && pipelineId && pipelineInternalId) {
      return dispatch(
        subscribeOnPipelineStatus(pipelineId, pipelineInternalId, internalBranch, timeout),
      )
    }
    return undefined
  }, [pipelineId, internalBranch, blockUpdates, timeout, dispatch, pipelineInternalId])
}
